import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MapPin, Briefcase, Building2, ExternalLink, ChevronDown, ChevronUp, Clock, Info, Sparkles, Loader2, CheckCircle } from 'lucide-react';
import api from '../api';
import { getCompanyColorPalette, getExperienceColor, getCompanyInitials } from '../utils/colorUtils';
import { getRelativeTime, estimateReadingTime } from '../utils/timeUtils';
import { parseJobDescription } from '../utils/descriptionParser';
import { calculateMatchScore } from '../utils/scoringUtils';

export default function JobCard({ job }) {
  const [imgFailed, setImgFailed] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [showScoreDetails, setShowScoreDetails] = useState(false);
  const [atsData, setAtsData] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);

  const handleAtsDeepDive = async (e) => {
    e.stopPropagation();

    // Read fresh user data from localStorage to ensure we have the latest resume
    let currentUser;
    try {
      currentUser = JSON.parse(localStorage.getItem('user') || '{}');
    } catch (e) { currentUser = {}; }

    if (!currentUser.resumeText) {
      alert("Please update your profile with a resume first to use the AI ATS Checker!");
      return;
    }

    setAnalyzing(true);
    try {
      // Build comprehensive job context with all available details
      const jobDetails = [];
      if (job.title) jobDetails.push(`Job Title: ${job.title}`);
      if (job.company) jobDetails.push(`Company: ${job.company}`);
      if (job.location) {
        const loc = typeof job.location === 'object'
          ? [job.location.city, job.location.region, job.location.countryName].filter(Boolean).join(', ')
          : job.location;
        jobDetails.push(`Location: ${loc}`);
      }
      if (job.experience) jobDetails.push(`Experience Required: ${job.experience}`);
      if (parsedDescription?.technologies?.length > 0) {
        jobDetails.push(`Technologies: ${parsedDescription.technologies.join(', ')}`);
      }
      if (parsedDescription?.rawText) {
        jobDetails.push(`\nJob Description:\n${parsedDescription.rawText}`);
      }

      const jobContext = jobDetails.length > 0
        ? jobDetails.join('\n')
        : `Job Title: ${job.title || 'Unknown'}\nCompany: ${job.company || 'Unknown'}`;

      console.log("Sending ATS Check Request:", {
        resumeLength: currentUser.resumeText.length,
        jobContextLength: jobContext.length
      });

      const res = await api.post('/ai/ats-check', {
        resumeText: currentUser.resumeText,
        jobDescription: jobContext
      });
      setAtsData(res.data);
    } catch (err) {
      console.error("ATS Check failed", err);
      const errorMessage = err.response?.data?.error || "Failed to analyze. Please try again or check if backend is running with Gemini API key.";
      alert(`Error: ${errorMessage}`);
    } finally {
      setAnalyzing(false);
    }
  };

  // Dynamic company color based on company name
  const companyColors = useMemo(() => getCompanyColorPalette(job.company), [job.company]);

  // Parse job description
  const parsedDescription = useMemo(() => parseJobDescription(job.description), [job.description]);

  // Calculate Match Score based on user profile
  const user = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('user') || '{}');
    } catch (e) { return {}; }
  }, []);

  const matchData = useMemo(() => calculateMatchScore(parsedDescription /* pass parsed for better accuracy */, user), [parsedDescription, user]);

  const formatLocation = (location) => {
    if (!location) return 'Remote';
    if (typeof location === 'object') {
      const { city, region, countryName } = location;
      const parts = [city, region].filter(Boolean);
      return parts.length > 0 ? parts.join(', ') : countryName || 'Remote';
    }
    return location;
  };

  const logoUrl = job.logo || '';
  const showPlaceholder = imgFailed || !logoUrl;
  const experienceColor = getExperienceColor(job.experience);
  const relativeTime = getRelativeTime(job.date_posted);
  const readingTime = estimateReadingTime(parsedDescription.rawText);
  const technologies = parsedDescription.technologies.slice(0, 4);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      whileHover={{ y: -8, scale: 1.01 }}
      transition={{ type: "spring", stiffness: 300, damping: 20 }}
      className="card flex flex-col h-full group relative overflow-visible"
      style={{
        borderTop: `3px solid ${companyColors.primary}`,
      }}
    >
      {/* Glow effect on hover */}
      <div
        className="absolute -inset-1 bg-gradient-to-r opacity-0 group-hover:opacity-20 blur-xl transition-opacity duration-500 -z-10 rounded-2xl"
        style={{
          backgroundImage: `linear-gradient(135deg, ${companyColors.primary}, ${companyColors.dark})`,
        }}
      />

      <div className="p-6 flex flex-col h-full relative">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 mb-4">
          {/* Company Logo with glow ring */}
          <div className="relative">
            <div
              className="absolute inset-0 rounded-lg blur-md opacity-0 group-hover:opacity-60 transition-opacity duration-300"
              style={{ background: companyColors.glow }}
            />
            <div
              className="relative w-14 h-14 flex-shrink-0 rounded-lg flex items-center justify-center p-2 border-2 transition-all duration-300"
              style={{
                backgroundColor: companyColors.light,
                borderColor: companyColors.primary,
              }}
            >
              {showPlaceholder ? (
                <div
                  className="w-full h-full flex items-center justify-center font-bold text-lg"
                  style={{ color: companyColors.primary }}
                >
                  {getCompanyInitials(job.company)}
                </div>
              ) : (
                <img
                  src={logoUrl}
                  alt={job.company}
                  className="w-full h-full object-contain"
                  onError={() => setImgFailed(true)}
                />
              )}
            </div>
          </div>

          {/* Match Score Ring (New Feature 🧠) */}
          <div
            className="relative flex flex-col items-end cursor-pointer group/score"
            onMouseEnter={() => setShowScoreDetails(true)}
            onMouseLeave={() => setShowScoreDetails(false)}
          >
            <div className="relative w-12 h-12 flex items-center justify-center">
              <MatchRing score={matchData.score} level={matchData.level} />
              <span className={`text-xs font-bold ${matchData.level === 'Strong' ? 'text-emerald-600' : matchData.level === 'Medium' ? 'text-amber-500' : 'text-slate-400'}`}>
                {matchData.score}%
              </span>
            </div>

            {/* Tooltip Panel */}
            <AnimatePresence>
              {showScoreDetails && (
                <motion.div
                  initial={{ opacity: 0, y: 10, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  className="absolute right-0 top-14 z-50 w-64 bg-white dark:bg-slate-800 rounded-xl shadow-xl p-4 border border-slate-100 dark:border-slate-700 pointer-events-none md:pointer-events-auto"
                >
                  <div className="flex items-center gap-2 mb-2 pb-2 border-b border-slate-100 dark:border-slate-700">
                    <Sparkles className="w-4 h-4 text-indigo-500" />
                    <span className="font-semibold text-sm text-slate-900 dark:text-slate-100">AI Match Analysis</span>
                  </div>
                  <div className="space-y-2">
                    {matchData.explanations.length > 0 ? (
                      matchData.explanations.slice(0, 3).map((exp, idx) => (
                        <div key={idx} className="flex items-start gap-2 text-xs text-slate-600 dark:text-slate-300">
                          <div className="w-1 h-1 mt-1.5 rounded-full bg-indigo-500 shrink-0" />
                          {exp}
                        </div>
                      ))
                    ) : (
                      <p className="text-xs text-slate-500 italic">Complete your profile to get detailed insights.</p>
                    )}
                  </div>
                  <div className="mt-3 pt-2 border-t border-slate-100 dark:border-slate-700 flex justify-between text-xs font-medium">
                    <span className="text-slate-500">Skills Match</span>
                    <span className="text-indigo-600 dark:text-indigo-400">{Math.round((matchData.breakdown.skills / 40) * 100)}%</span>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* Job Title */}
        <h3
          className="font-bold text-lg text-slate-900 dark:text-slate-100 mb-2 line-clamp-2 leading-tight transition-all duration-300"
        >
          {job.title}
        </h3>

        {/* Company Name */}
        <p className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-4 flex items-center gap-1.5">
          <Building2 className="w-3.5 h-3.5 text-slate-400" />
          {job.company}
        </p>

        {/* Technologies */}
        {technologies.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-4">
            {technologies.map((tech, idx) => (
              <span
                key={idx}
                className="px-2 py-0.5 text-xs font-medium rounded-md"
                style={{
                  backgroundColor: companyColors.light,
                  color: companyColors.dark,
                }}
              >
                {tech}
              </span>
            ))}
          </div>
        )}

        {/* Badges */}
        <div className="flex flex-wrap gap-2 mb-auto">
          <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300`}>
            <MapPin className="w-3 h-3 mr-1" />
            {formatLocation(job.location).substring(0, 50)}
            {formatLocation(job.location).length > 50 && '...'}
          </span>
          {job.experience && (
            <span
              className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-${experienceColor}-50 text-${experienceColor}-700`}
            >
              <Briefcase className="w-3 h-3 mr-1" />
              {job.experience.substring(0, 30)}
            </span>
          )}
          {relativeTime && (
            <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-800 border border-slate-100 dark:border-slate-700">
              <Clock className="w-3 h-3 mr-1" />
              {relativeTime}
            </span>
          )}
        </div>

        {/* Expandable Description */}
        {parsedDescription.rawText && (
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="w-full mt-4 text-left text-sm text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200 transition-colors flex items-center justify-between group/expand"
          >
            <span className="flex items-center gap-2">
              {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              <span className="font-medium">
                {isExpanded ? 'Hide Details' : 'View Details'}
              </span>
              {!isExpanded && readingTime && (
                <span className="text-xs text-slate-400">• {readingTime}</span>
              )}
            </span>
          </button>
        )}

        <AnimatePresence>
          {isExpanded && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="overflow-hidden mt-4 pt-4 border-t border-slate-100 dark:border-slate-700"
            >
              <JobDescriptionContent parsedData={parsedDescription} />
            </motion.div>
          )}
        </AnimatePresence>

        {/* Apply Button & ATS Check */}
        <div className="mt-4 flex flex-col gap-3">
          {job.apply_link && (
            <a
              href={job.apply_link}
              target="_blank"
              rel="noreferrer"
              className="w-full px-4 py-3 rounded-xl font-medium text-white text-center transition-all duration-300 flex items-center justify-center gap-2 group/btn relative overflow-hidden shadow-lg hover:shadow-xl hover:-translate-y-0.5"
              style={{
                background: `linear-gradient(135deg, ${companyColors.primary}, ${companyColors.dark})`,
              }}
            >
              <span className="relative z-10 flex items-center gap-2">
                Apply Now
                <ExternalLink className="w-4 h-4 group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5 transition-transform" />
              </span>
              <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -translate-x-full group-hover/btn:translate-x-full transition-transform duration-1000" />
            </a>
          )}

          <button
            onClick={handleAtsDeepDive}
            disabled={analyzing}
            className={`w-full px-4 py-2.5 rounded-xl font-medium text-sm transition-all flex items-center justify-center gap-2 border ${atsData ? 'bg-indigo-50 border-indigo-200 text-indigo-700 dark:bg-indigo-900/30 dark:border-indigo-700 dark:text-indigo-300' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-700'}`}
          >
            {analyzing ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin text-indigo-500" />
                Analyzing with AI...
              </>
            ) : atsData ? (
              <>
                <CheckCircle className="w-4 h-4 text-emerald-500" />
                View ATS Report ({atsData.atsScore}%)
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4 text-purple-500" />
                Check ATS Score
              </>
            )}
          </button>
        </div>

        {/* ATS Analysis Report */}
        <AnimatePresence>
          {atsData && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="mt-3 bg-indigo-50/50 dark:bg-indigo-900/10 rounded-xl border border-indigo-100 dark:border-indigo-800 p-4 overflow-hidden"
            >
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-bold text-slate-800 dark:text-slate-200 text-sm flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-indigo-500" />
                  AI Recruiter Feedback
                </h4>
                <span className={`text-xs font-bold px-2 py-1 rounded bg-white dark:bg-slate-800 border ${atsData.atsScore >= 70 ? 'border-emerald-200 text-emerald-600' : 'border-amber-200 text-amber-600'}`}>
                  {atsData.atsScore}/100
                </span>
              </div>

              <p className="text-xs text-slate-600 dark:text-slate-400 mb-3 italic">
                "{atsData.analysis}"
              </p>

              <div className="space-y-3">
                <div>
                  <h5 className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-1">Missing Keywords</h5>
                  <div className="flex flex-wrap gap-1">
                    {atsData.missingKeywords?.map((kw, i) => (
                      <span key={i} className="text-[10px] px-1.5 py-0.5 bg-red-50 text-red-600 border border-red-100 rounded dark:bg-red-900/20 dark:text-red-300 dark:border-red-800">
                        {kw}
                      </span>
                    ))}
                  </div>
                </div>

                <div>
                  <h5 className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-1">Smart Fixes</h5>
                  <ul className="space-y-2">
                    {atsData.recommendations?.map((rec, i) => (
                      <li key={i} className="text-xs text-slate-700 dark:text-slate-300 flex items-start gap-2 leading-relaxed">
                        <span className="text-indigo-500 font-bold mt-0.5">•</span>
                        <span>{rec.replace(/\*\*/g, '')}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
}

// Circular Progress Ring Component
function MatchRing({ score, level }) {
  const radius = 20;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (score / 100) * circumference;

  // Color determination
  const colorClass = level === 'Strong' ? 'text-emerald-500' :
    level === 'Medium' ? 'text-amber-500' : 'text-slate-300';

  return (
    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
      <svg className="transform -rotate-90 w-full h-full p-0.5" viewBox="0 0 48 48">
        {/* Background Ring */}
        <circle
          cx="24" cy="24" r={radius}
          fill="transparent"
          stroke="currentColor"
          strokeWidth="3"
          className="text-slate-100 dark:text-slate-700"
        />

        {/* Progress Ring */}
        <motion.circle
          initial={{ strokeDashoffset: circumference }}
          animate={{ strokeDashoffset }}
          transition={{ duration: 1, ease: "easeOut", delay: 0.2 }}
          cx="24" cy="24" r={radius}
          fill="transparent"
          stroke="currentColor"
          strokeWidth="3"
          strokeDasharray={circumference}
          strokeLinecap="round"
          className={`${colorClass}`}
        />
      </svg>
    </div>
  );
}

// Job Description Content Component
function JobDescriptionContent({ parsedData }) {
  if (!parsedData) return null;

  const { requirements, responsibilities, benefits, technologies } = parsedData;

  return (
    <div className="space-y-5 text-sm">
      {/* All technologies */}
      {technologies.length > 0 && (
        <div>
          <h5 className="font-semibold text-slate-900 dark:text-slate-100 mb-2.5 flex items-center gap-2">
            <span className="w-1 h-4 bg-indigo-500 rounded-full"></span>
            Tech Stack
          </h5>
          <div className="flex flex-wrap gap-1.5">
            {technologies.map((tech, idx) => (
              <span
                key={idx}
                className="px-2 py-1 bg-indigo-50 text-indigo-700 text-xs font-medium rounded-md border border-indigo-100"
              >
                {tech}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Responsibilities */}
      {responsibilities.length > 0 && (
        <DetailSection title="Responsibilities" items={responsibilities} color="blue" />
      )}

      {/* Requirements */}
      {requirements.length > 0 && (
        <DetailSection title="Requirements" items={requirements} color="purple" />
      )}

      {/* Benefits */}
      {benefits.length > 0 && (
        <DetailSection title="Benefits" items={benefits} color="emerald" />
      )}

      {/* Fallback to raw text if no structured data */}
      {(!requirements.length && !responsibilities.length && !benefits.length) && parsedData.rawText && (
        <div className="text-slate-600 dark:text-slate-400 text-xs leading-relaxed space-y-1.5">
          {parsedData.rawText
            .split('\n')
            .filter(line => {
              const lower = line.toLowerCase();
              // Filter out legal boilerplate and generic text
              return line.trim() &&
                !lower.includes('equal opportunity employer') &&
                !lower.includes('does not discriminate') &&
                !lower.includes('inclusive culture') &&
                !lower.includes('disability and need a workplace') &&
                !lower.includes('compensation reflects') &&
                !lower.includes('base pay for this position') &&
                !lower.includes('amazon is a total compensation') &&
                !lower.includes('for more information, please visit') &&
                !lower.includes('this position will remain posted') &&
                !lower.startsWith('los angeles county applicants') &&
                lower.length < 200; // Skip very long paragraphs (usually legal text)
            })
            .slice(0, 5) // Show only first 5 relevant lines
            .map((line, idx) => (
              <p key={idx} className="text-left leading-relaxed">{line.trim()}</p>
            ))
          }
        </div>
      )}
    </div>
  );
}

function DetailSection({ title, items, color }) {
  const colorMap = {
    blue: { bg: 'bg-blue-500', text: 'text-blue-500' },
    purple: { bg: 'bg-purple-500', text: 'text-purple-500' },
    emerald: { bg: 'bg-emerald-500', text: 'text-emerald-500' },
  };

  const colors = colorMap[color] || colorMap.blue;

  return (
    <div>
      <h5 className="font-semibold text-slate-900 dark:text-slate-100 mb-2.5 flex items-center gap-2">
        <span className={`w-1 h-4 ${colors.bg} rounded-full`}></span>
        {title}
      </h5>
      <ul className="space-y-2 ml-1">
        {items.slice(0, 3).map((item, idx) => (
          <li key={idx} className="flex items-start gap-2.5 text-slate-700 dark:text-slate-300">
            <span className={`mt-1 w-1.5 h-1.5 rounded-full flex-shrink-0 ${colors.bg}`}></span>
            <span className="text-xs leading-relaxed flex-1">{item}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
